import React, { useState, useEffect, useCallback } from 'react';
import { clientesAPI, citasAPI, consultasAPI, usuariosAPI, backupsAPI, demoSeedAPI, authAPI, tareasAPI, facturacionStatsAPI, configuracionAPI, oportunidadesAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { getLabels } from '../utils/userLabels';
import { getClienteColor as getBaseClienteColor } from '../utils/clienteColors';
import './Dashboard.css';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [backupStats, setBackupStats] = useState(null);
  const [clientesActivos, setClientesActivos] = useState(0);
  const [clientes, setClientes] = useState([]);
  const labels = getLabels(user?.tipo_usuario || 'coach');
  const [citas, setCitas] = useState([]);
  const [cumpleanos, setCumpleanos] = useState([]);
  const [tareasProximas, setTareasProximas] = useState([]);
  const [facturacionStats, setFacturacionStats] = useState(null);
  const [oportunidadesStats, setOportunidadesStats] = useState(null);
  const [stats, setStats] = useState({
    citasHoy: 0,
    citasManana: 0,
    sesionesEsteMes: 0,
    citasCanceladas: 0,
    citasConfirmadas: 0,
    citasCompletadas: 0,
    modalidadPresencial: 0,
    modalidadOnline: 0,
    sesionesOnline: 0,
    sesionesPresencial: 0,
    clientesInactivos: 0,
    totalCitasCompletadas: 0,
    tiempoTotalInvertido: 0, // en minutos
    sesionesUltimoMes: 0,
    tiempoUltimoMes: 0, // en minutos
    tiempoMesActual: 0 // en minutos
  });
  const [error, setError] = useState(null);
  const [demoSeedLoading, setDemoSeedLoading] = useState(false);
  const [demoSeedMessage, setDemoSeedMessage] = useState(null);
  const [modoMantenimiento, setModoMantenimiento] = useState(false);
  const [cambiandoMantenimiento, setCambiandoMantenimiento] = useState(false);
  const [dashConfig, setDashConfig] = useState({ order: ['kpis','quickStats','facturacion','oportunidades','citas','comparativa','tareas','cumpleanos','resumenGlobal'], hidden: [] });
  const [customizeMode, setCustomizeMode] = useState(false);
  const [dragWidget, setDragWidget] = useState(null);
  const [dragOverWidget, setDragOverWidget] = useState(null);

  // Resetear scroll al montar para evitar saltos visuales
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Cargar configuración de widgets desde localStorage
  const WIDGET_IDS = ['kpis','quickStats','facturacion','oportunidades','citas','comparativa','tareas','cumpleanos','resumenGlobal'];
  useEffect(() => {
    const key = `dashboard-config-${user?.id || 'default'}`;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const cfg = JSON.parse(raw);
        const order = (cfg.order || []).filter(id => WIDGET_IDS.includes(id));
        WIDGET_IDS.forEach(id => { if (!order.includes(id)) order.push(id); });
        setDashConfig({ order, hidden: cfg.hidden || [] });
      } else {
        setDashConfig({ order: WIDGET_IDS, hidden: [] });
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Calcular fechas de inicio y fin para las próximas 2 semanas
  const getFechasSemanas = () => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    // Inicio: hoy (usar métodos locales para evitar problemas de zona horaria)
    const añoInicio = hoy.getFullYear();
    const mesInicio = String(hoy.getMonth() + 1).padStart(2, '0');
    const diaInicio = String(hoy.getDate()).padStart(2, '0');
    const fechaInicio = `${añoInicio}-${mesInicio}-${diaInicio}`;
    
    // Fin: dentro de 14 días (2 semanas)
    const fechaFin = new Date(hoy);
    fechaFin.setDate(fechaFin.getDate() + 14);
    const añoFin = fechaFin.getFullYear();
    const mesFin = String(fechaFin.getMonth() + 1).padStart(2, '0');
    const diaFin = String(fechaFin.getDate()).padStart(2, '0');
    const fechaFinStr = `${añoFin}-${mesFin}-${diaFin}`;
    
    return { fechaInicio, fechaFin: fechaFinStr };
  };

  // Helper de seguridad: asegurar que siempre trabajamos con arrays
  const toArray = (value) => (Array.isArray(value) ? value : []);

  // Obtener clientes activos
  const loadClientesActivos = useCallback(async () => {
    try {
      // Cargar solo clientes activos para el contador y cumpleaños
      const response = await clientesAPI.getAll(true);
      const clientesData = toArray(response.data);
      setClientesActivos(clientesData.length);
      
      // Cargar TODOS los clientes (activos e inactivos) para calcular clientes inactivos
      const responseTodos = await clientesAPI.getAll(false);
      const todosLosClientes = toArray(responseTodos.data);
      setClientes(todosLosClientes); // Guardar todos los clientes para el cálculo de inactivos
      
      // Calcular clientes inactivos (clientes marcados como inactivos en su ficha)
      // Un cliente inactivo es simplemente aquel que tiene activo = false o activo = 0
      const clientesInactivos = todosLosClientes.filter(c => {
        // Un cliente está inactivo si activo es false, 0, '0', o null
        const activo = c.activo;
        return activo === false || activo === 0 || activo === '0' || activo === null;
      }).length;
      
      setStats(prev => ({
        ...prev,
        clientesInactivos
      }));
      
      // Filtrar cumpleaños en las próximas 2 semanas
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const finSemanas = new Date(hoy);
      finSemanas.setDate(finSemanas.getDate() + 14);
      
      const cumpleanosFiltrados = clientesData
        .filter(cliente => {
          if (!cliente.fecha_nacimiento) return false;
          
          // Obtener año actual para comparar
          const fechaNac = new Date(cliente.fecha_nacimiento);
          const cumpleanosEsteAno = new Date(
            hoy.getFullYear(),
            fechaNac.getMonth(),
            fechaNac.getDate()
          );
          
          // Si el cumpleaños ya pasó este año, usar el del próximo año
          if (cumpleanosEsteAno < hoy) {
            cumpleanosEsteAno.setFullYear(hoy.getFullYear() + 1);
          }
          
          // Verificar si está en el rango de las próximas 2 semanas
          return cumpleanosEsteAno >= hoy && cumpleanosEsteAno <= finSemanas;
        })
        .map(cliente => {
          const fechaNac = new Date(cliente.fecha_nacimiento);
          const cumpleanosEsteAno = new Date(
            hoy.getFullYear(),
            fechaNac.getMonth(),
            fechaNac.getDate()
          );
          
          if (cumpleanosEsteAno < hoy) {
            cumpleanosEsteAno.setFullYear(hoy.getFullYear() + 1);
          }
          
          return {
            ...cliente,
            fechaCumpleanos: cumpleanosEsteAno
          };
        })
        .sort((a, b) => {
          // Ordenar por fecha de cumpleaños
          const fechaA = a.fechaCumpleanos;
          const fechaB = b.fechaCumpleanos;
          
          // Comparar mes y día
          if (fechaA.getMonth() !== fechaB.getMonth()) {
            return fechaA.getMonth() - fechaB.getMonth();
          }
          return fechaA.getDate() - fechaB.getDate();
        });
      
      setCumpleanos(cumpleanosFiltrados);
    } catch (err) {
      console.error('Error al cargar clientes:', err);
      setError('Error al cargar datos de clientes');
    }
  }, []);

  // Obtener citas de las próximas 2 semanas y calcular estadísticas
  const loadCitas = useCallback(async () => {
    try {
      const { fechaInicio, fechaFin } = getFechasSemanas();
      const response = await citasAPI.getAll(null, null, null); // Obtener todas las citas para estadísticas
      const todasLasCitas = toArray(response.data);
      
      // Filtrar citas de las próximas 2 semanas
      const citasData = todasLasCitas.filter(cita => {
        const citaFechaObj = new Date(cita.fecha_hora);
        const citaAño = citaFechaObj.getFullYear();
        const citaMes = String(citaFechaObj.getMonth() + 1).padStart(2, '0');
        const citaDia = String(citaFechaObj.getDate()).padStart(2, '0');
        const fechaCita = `${citaAño}-${citaMes}-${citaDia}`;
        return fechaCita >= fechaInicio && fechaCita <= fechaFin;
      });
      
      // Ordenar citas por fecha
      const citasOrdenadas = citasData.sort((a, b) => {
        return new Date(a.fecha_hora) - new Date(b.fecha_hora);
      });
      
      setCitas(citasOrdenadas);
      
      // Calcular estadísticas
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const manana = new Date(hoy);
      manana.setDate(manana.getDate() + 1);
      
      // Usar métodos locales para evitar problemas de zona horaria
      const hoyAño = hoy.getFullYear();
      const hoyMes = String(hoy.getMonth() + 1).padStart(2, '0');
      const hoyDia = String(hoy.getDate()).padStart(2, '0');
      const hoyStr = `${hoyAño}-${hoyMes}-${hoyDia}`;
      
      const mananaAño = manana.getFullYear();
      const mananaMes = String(manana.getMonth() + 1).padStart(2, '0');
      const mananaDia = String(manana.getDate()).padStart(2, '0');
      const mananaStr = `${mananaAño}-${mananaMes}-${mananaDia}`;
      
      // Primer día del mes actual
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      const inicioMesAño = inicioMes.getFullYear();
      const inicioMesMes = String(inicioMes.getMonth() + 1).padStart(2, '0');
      const inicioMesDia = String(inicioMes.getDate()).padStart(2, '0');
      const inicioMesStr = `${inicioMesAño}-${inicioMesMes}-${inicioMesDia}`;
      
      const citasHoy = todasLasCitas.filter(cita => {
        const citaFechaObj = new Date(cita.fecha_hora);
        const citaAño = citaFechaObj.getFullYear();
        const citaMes = String(citaFechaObj.getMonth() + 1).padStart(2, '0');
        const citaDia = String(citaFechaObj.getDate()).padStart(2, '0');
        const fechaCita = `${citaAño}-${citaMes}-${citaDia}`;
        return fechaCita === hoyStr;
      }).length;
      
      const citasManana = todasLasCitas.filter(cita => {
        const citaFechaObj = new Date(cita.fecha_hora);
        const citaAño = citaFechaObj.getFullYear();
        const citaMes = String(citaFechaObj.getMonth() + 1).padStart(2, '0');
        const citaDia = String(citaFechaObj.getDate()).padStart(2, '0');
        const fechaCita = `${citaAño}-${citaMes}-${citaDia}`;
        return fechaCita === mananaStr;
      }).length;
      
      const citasEsteMes = todasLasCitas.filter(cita => {
        const citaFechaObj = new Date(cita.fecha_hora);
        const citaAño = citaFechaObj.getFullYear();
        const citaMes = String(citaFechaObj.getMonth() + 1).padStart(2, '0');
        const citaDia = String(citaFechaObj.getDate()).padStart(2, '0');
        const fechaCita = `${citaAño}-${citaMes}-${citaDia}`;
        return fechaCita >= inicioMesStr;
      });
      
      // Contar todas las citas canceladas (no solo del mes)
      const citasCanceladas = todasLasCitas.filter(c => c.estado === 'cancelada').length;
      const citasConfirmadas = citasEsteMes.filter(c => c.estado === 'confirmada').length;
      const citasCompletadas = citasEsteMes.filter(c => c.estado === 'completada').length;
      
      // Calcular tiempo total invertido en el mes actual
      const tiempoMesActual = citasEsteMes.filter(c => c.estado === 'completada').reduce((total, cita) => {
        const duracion = cita.duracion_minutos || cita.duracion || 60;
        return total + (parseInt(duracion) || 60);
      }, 0);
      
      // Calcular datos del último mes natural
      const ultimoMes = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
      const finUltimoMes = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      const inicioUltimoMesAño = ultimoMes.getFullYear();
      const inicioUltimoMesMes = String(ultimoMes.getMonth() + 1).padStart(2, '0');
      const inicioUltimoMesDia = String(ultimoMes.getDate()).padStart(2, '0');
      const inicioUltimoMesStr = `${inicioUltimoMesAño}-${inicioUltimoMesMes}-${inicioUltimoMesDia}`;
      const finUltimoMesAño = finUltimoMes.getFullYear();
      const finUltimoMesMes = String(finUltimoMes.getMonth() + 1).padStart(2, '0');
      const finUltimoMesDia = String(finUltimoMes.getDate()).padStart(2, '0');
      const finUltimoMesStr = `${finUltimoMesAño}-${finUltimoMesMes}-${finUltimoMesDia}`;
      
      const citasUltimoMes = todasLasCitas.filter(cita => {
        const citaFechaObj = new Date(cita.fecha_hora);
        const citaAño = citaFechaObj.getFullYear();
        const citaMes = String(citaFechaObj.getMonth() + 1).padStart(2, '0');
        const citaDia = String(citaFechaObj.getDate()).padStart(2, '0');
        const fechaCita = `${citaAño}-${citaMes}-${citaDia}`;
        return fechaCita >= inicioUltimoMesStr && fechaCita <= finUltimoMesStr;
      });
      
      const sesionesUltimoMes = citasUltimoMes.filter(c => c.estado === 'completada').length;
      const tiempoUltimoMes = citasUltimoMes.filter(c => c.estado === 'completada').reduce((total, cita) => {
        const duracion = cita.duracion_minutos || cita.duracion || 60;
        return total + (parseInt(duracion) || 60);
      }, 0);
      
      // Contar clientes activos por modalidad preferida (no por citas del mes)
      // Esto asegura que todos los clientes activos se cuenten y la suma coincida con el total
      // Necesitamos cargar los clientes aquí para contar por modalidad preferida
      const clientesResponse = await clientesAPI.getAll(false);
      const todosLosClientes = toArray(clientesResponse.data);
      const clientesActivosData = todosLosClientes.filter(c => 
        c.activo === true || c.activo === 1 || c.activo === '1'
      );
      
      const modalidadPresencial = clientesActivosData.filter(c => 
        c.modalidad_preferida === 'Presencial'
      ).length;
      
      const modalidadOnline = clientesActivosData.filter(c => 
        c.modalidad_preferida === 'Online'
      ).length;
      
      // Calcular total de citas completadas (todas, no solo del mes)
      const todasCitasCompletadas = todasLasCitas.filter(c => c.estado === 'completada');
      const totalCitasCompletadas = todasCitasCompletadas.length;
      
      // Calcular tiempo total invertido (suma de duraciones de todas las citas completadas)
      const tiempoTotalInvertido = todasCitasCompletadas.reduce((total, cita) => {
        // Intentar obtener duración de diferentes campos posibles
        const duracion = cita.duracion_minutos || cita.duracion || 60; // Por defecto 60 minutos si no tiene duración
        return total + (parseInt(duracion) || 60);
      }, 0);
      
      // Actualizar solo las métricas relacionadas con citas, preservando otras como clientesInactivos
      setStats(prev => ({
        ...prev,
        citasHoy,
        citasManana,
        // sesionesEsteMes se recalcula en loadSesionesStats
        sesionesEsteMes: prev.sesionesEsteMes,
        citasCanceladas,
        citasConfirmadas,
        citasCompletadas,
        modalidadPresencial,
        modalidadOnline,
        // clientesInactivos se calcula en loadClientesActivos y no debe pisarse aquí
        totalCitasCompletadas,
        tiempoTotalInvertido,
        sesionesUltimoMes,
        tiempoUltimoMes,
        tiempoMesActual
      }));
    } catch (err) {
      console.error('Error al cargar citas:', err);
      setError('Error al cargar citas');
    }
  }, []);
  
  // Cargar sesiones para estadísticas
  const loadSesionesStats = useCallback(async () => {
    try {
      const response = await consultasAPI.getAll();
      const sesiones = toArray(response.data);
      
      // Calcular sesiones de este mes
      const hoy = new Date();
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      const inicioMesStr = inicioMes.toISOString().split('T')[0];
      
      const sesionesEsteMes = sesiones.filter(sesion => {
        const fechaSesion = sesion.fecha_consulta ? sesion.fecha_consulta.split('T')[0] : null;
        return fechaSesion && fechaSesion >= inicioMesStr;
      }).length;
      
      // Contar sesiones por modalidad (todas las sesiones, no solo del mes)
      const sesionesOnline = sesiones.filter(sesion => 
        sesion.modalidad === 'Online' || sesion.modalidad === 'online'
      ).length;
      
      const sesionesPresencial = sesiones.filter(sesion => 
        sesion.modalidad === 'Presencial' || sesion.modalidad === 'presencial' || !sesion.modalidad
      ).length;
      
      setStats(prev => ({
        ...prev,
        sesionesEsteMes,
        sesionesOnline,
        sesionesPresencial
      }));
    } catch (err) {
      console.error('Error al cargar estadísticas de sesiones:', err);
    }
  }, []);


  // Cargar información del usuario - solo una vez al montar
  useEffect(() => {
    let mounted = true;
    authAPI.check()
      .then(response => {
        if (mounted && response.data.authenticated && response.data.user) {
          const userData = response.data.user;
          setUser(userData);
          setIsAdmin(userData.rol === 'admin');
        }
      })
      .catch(err => {
        if (mounted) {
          console.error('Error al verificar usuario:', err);
        }
      });
    return () => { mounted = false; };
  }, []);

  // Cargar tareas próximas si el módulo está habilitado
  const loadTareasProximas = useCallback(async () => {
    if (!user || !(user.tareas_habilitadas === true || user.tareas_habilitadas === 1)) {
      return;
    }
    
    try {
      const response = await tareasAPI.getProximas(14);
      const data = response.data?.data ?? response.data ?? [];
      setTareasProximas(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar tareas próximas:', err);
      setTareasProximas([]);
    }
  }, [user]);

  useEffect(() => {
    loadTareasProximas();
  }, [loadTareasProximas]);

  // Cargar estadísticas de facturación si el módulo está habilitado
  const loadFacturacionStats = useCallback(async () => {
    if (!user || !(user.facturacion_habilitada === true || user.facturacion_habilitada === 1)) {
      return;
    }
    
    try {
      const response = await facturacionStatsAPI.getStats();
      const data = response.data?.data ?? response.data ?? {};
      setFacturacionStats(data);
    } catch (err) {
      console.error('Error al cargar estadísticas de facturación:', err);
      setFacturacionStats(null);
    }
  }, [user]);

  useEffect(() => {
    loadFacturacionStats();
  }, [loadFacturacionStats]);

  const loadOportunidadesStats = useCallback(async () => {
    if (!user || !(user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1)) {
      return;
    }
    try {
      const response = await oportunidadesAPI.getStats();
      setOportunidadesStats(response.data?.data ?? response.data ?? null);
    } catch (err) {
      console.error('Error al cargar estadísticas de oportunidades:', err);
      setOportunidadesStats(null);
    }
  }, [user]);

  useEffect(() => {
    loadOportunidadesStats();
  }, [loadOportunidadesStats]);

  // Cargar número de usuarios si es admin
  const loadUsuarios = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const response = await usuariosAPI.getAll(null);
      const usuarios = toArray(response.data);
      setTotalUsuarios(usuarios.length);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  }, [isAdmin]);

  // Cargar estadísticas de backups si es admin
  const loadBackupStats = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const response = await backupsAPI.getStats();
      const stats = response.data;
      if (Array.isArray(stats)) {
        return;
      }
      setBackupStats(stats);
    } catch (err) {
      setBackupStats(null);
    }
  }, [isAdmin]);

  const loadModoMantenimiento = useCallback(async () => {
    try {
      const r = await configuracionAPI.getModoMantenimiento();
      setModoMantenimiento((r.data?.modo_mantenimiento ?? 0) === 1);
    } catch (e) {
      setModoMantenimiento(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      // No mostrar loading para evitar saltos visuales
      if (!mounted) return;
      setError(null);
      
      if (isAdmin) {
        loadModoMantenimiento();
        // Admin carga usuarios y backups
        await Promise.all([
          loadUsuarios(),
          loadBackupStats()
        ]);
      } else {
        // Coach carga datos normales
        await Promise.all([
          loadClientesActivos(),
          loadCitas()
        ]);
      }
    };
    
    if (user !== null) { // Esperar a que se cargue el usuario
      loadData();
    }
    
    return () => { mounted = false; };
  }, [isAdmin, user, loadClientesActivos, loadCitas, loadUsuarios, loadBackupStats, loadModoMantenimiento]);

  // Recargar datos cuando la página vuelve a tener foco (por si se borró un cliente en otra pestaña)
  useEffect(() => {
    const handleFocus = () => {
      if (user !== null && !isAdmin) {
        loadClientesActivos();
        loadCitas();
        loadTareasProximas();
        loadFacturacionStats();
        loadOportunidadesStats();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, isAdmin, loadClientesActivos, loadCitas, loadTareasProximas, loadFacturacionStats, loadOportunidadesStats]);
  
  useEffect(() => {
    if (clientes.length > 0) {
      loadSesionesStats();
    }
  }, [clientes, loadSesionesStats]);

  // Agrupar citas por día
  const getCitasPorDia = (fecha) => {
    // Usar métodos locales para evitar problemas de zona horaria
    const año = fecha.getFullYear();
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    const fechaStr = `${año}-${mes}-${dia}`;
    
    const citasDia = citas.filter(cita => {
      const citaFechaObj = new Date(cita.fecha_hora);
      const citaAño = citaFechaObj.getFullYear();
      const citaMes = String(citaFechaObj.getMonth() + 1).padStart(2, '0');
      const citaDia = String(citaFechaObj.getDate()).padStart(2, '0');
      const citaFecha = `${citaAño}-${citaMes}-${citaDia}`;
      return citaFecha === fechaStr;
    });
    
    // Ordenar por hora
    return citasDia.sort((a, b) => {
      return new Date(a.fecha_hora) - new Date(b.fecha_hora);
    });
  };

  // Obtener días de las próximas 2 semanas
  const getDiasSemanas = () => {
    const dias = [];
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 14; i++) {
      const fecha = new Date(hoy);
      fecha.setDate(fecha.getDate() + i);
      dias.push(fecha);
    }
    
    return dias;
  };

  // Formatear fecha para mostrar
  const formatearFecha = (fecha) => {
    return fecha.toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  // Obtener nombre del cliente (helper)
  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellidos}` : `Cliente #${clienteId}`;
  };

  const getClienteColor = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return getBaseClienteColor(clienteId, cliente?.color);
  };

  // Función helper para formatear bytes
  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const pow = Math.floor(Math.log(bytes) / Math.log(1024));
    const pow2 = Math.min(pow, units.length - 1);
    return (bytes / Math.pow(1024, pow2)).toFixed(2) + ' ' + units[pow2];
  };

  // Función helper para formatear tiempo (minutos a horas y minutos)
  const formatTiempo = (minutos) => {
    if (!minutos || minutos === 0) return '0h 0m';
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    if (horas === 0) {
      return `${mins}m`;
    }
    if (mins === 0) {
      return `${horas}h`;
    }
    return `${horas}h ${mins}m`;
  };

  const handleToggleMantenimiento = async () => {
    setCambiandoMantenimiento(true);
    try {
      const nuevo = !modoMantenimiento;
      await configuracionAPI.setModoMantenimiento(nuevo);
      setModoMantenimiento(nuevo);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cambiar modo mantenimiento');
    } finally {
      setCambiandoMantenimiento(false);
    }
  };

  const handleRegenerateDemo = async () => {
    setDemoSeedLoading(true);
    setDemoSeedMessage(null);
    try {
      const r = await demoSeedAPI.regenerate();
      setDemoSeedMessage(r.data?.message || 'Datos regenerados.');
    } catch (e) {
      setDemoSeedMessage(e.response?.data?.error || 'Error al regenerar.');
    } finally {
      setDemoSeedLoading(false);
    }
  };

  const formatCurrency = (val) =>
    (val || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const getTrend = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? { pct: 100, dir: 'up' } : { pct: 0, dir: 'neutral' };
    const pct = Math.round(((current - previous) / previous) * 100);
    return { pct: Math.abs(pct), dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral' };
  };

  const getRevenueChartData = () => {
    if (!facturacionStats?.ingresos_por_mes) return [];
    const meses = facturacionStats.ingresos_por_mes.slice(-6);
    if (meses.length === 0) return [];
    const max = Math.max(...meses.map(m => m.total), 1);
    return meses.map(m => ({
      label: new Date(m.mes + '-01').toLocaleDateString('es-ES', { month: 'short' }),
      value: m.total,
      pct: Math.round((m.total / max) * 100)
    }));
  };

  // Dashboard para Admin
  if (isAdmin) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h2>Dashboard Administrador</h2>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Métricas para Admin */}
        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <div className="stat-value">{totalUsuarios}</div>
              <div className="stat-label">Usuarios Totales</div>
            </div>
            <Link to="/usuarios" className="stat-link">Ver →</Link>
          </div>
          
          {backupStats !== null && (
            <>
              <div className="dashboard-stat-card">
                <div className="stat-icon">💾</div>
                <div className="stat-content">
                  <div className="stat-value">{backupStats.total ?? 0}</div>
                  <div className="stat-label">Backups Totales</div>
                </div>
                <Link to="/backups" className="stat-link">Ver →</Link>
              </div>
              
              <div className="dashboard-stat-card">
                <div className="stat-icon">📦</div>
                <div className="stat-content">
                  <div className="stat-value">
                    {backupStats.total_size 
                      ? formatBytes(backupStats.total_size)
                      : '0 B'}
                  </div>
                  <div className="stat-label">Espacio Usado</div>
                </div>
              </div>
            </>
          )}
        </div>

        {demoSeedMessage && (
          <div className={`alert ${demoSeedMessage.startsWith('Error') ? 'alert-error' : 'alert alert-success'}`} style={{ marginBottom: '1rem' }}>
            {demoSeedMessage}
          </div>
        )}

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>Modo Mantenimiento</h3>
            </div>
            <div className="dashboard-card-content">
              <p>
                Cuando está activo, los coaches no podrán acceder al sistema. Solo el administrador puede entrar para desactivarlo.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={modoMantenimiento}
                  onChange={handleToggleMantenimiento}
                  disabled={cambiandoMantenimiento}
                />
                <span>{modoMantenimiento ? 'Activo (sistema en mantenimiento)' : 'Desactivado'}</span>
              </label>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>Gestión de Usuarios</h3>
            </div>
            <div className="dashboard-card-content">
              <p>Desde aquí puedes gestionar todos los usuarios del sistema.</p>
              <Link to="/usuarios" className="btn btn-primary" style={{ marginTop: '15px', display: 'inline-block' }}>
                Gestionar Usuarios
              </Link>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>Coach demo</h3>
            </div>
            <div className="dashboard-card-content">
              <p>
                Usuario de demostración (<strong>demo</strong> / <strong>admin123</strong>) con clientes, citas y sesiones ficticias.
                Las fechas se generan respecto a hoy, así que las demos no quedan antiguas.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRegenerateDemo}
                disabled={demoSeedLoading}
                style={{ marginTop: '15px', display: 'inline-block' }}
              >
                {demoSeedLoading ? 'Regenerando…' : 'Regenerar datos ficticios'}
              </button>
            </div>
          </div>
          
          {backupStats !== null && (
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3>Backups del Sistema</h3>
              </div>
              <div className="dashboard-card-content">
                <div className="summary-stats">
                  <div className="summary-item">
                    <span className="summary-label">Total de Backups:</span>
                    <span className="summary-value">{backupStats.total ?? 0}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Espacio Total:</span>
                    <span className="summary-value">
                      {backupStats.total_size 
                        ? formatBytes(backupStats.total_size)
                        : '0 B'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Último Backup:</span>
                    <span className="summary-value">
                      {backupStats.last_backup
                        ? new Date(backupStats.last_backup).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Nunca'}
                    </span>
                  </div>
                </div>
                <Link to="/backups" className="btn btn-primary" style={{ marginTop: '15px', display: 'inline-block' }}>
                  Gestionar Backups
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Dashboard para Coach — personalizable con drag & drop
  const diasSemanas = getDiasSemanas();
  const facHabilitada = user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1);
  const oportHabilitadas = user && (user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1);
  const tareasHabilitadas = user && (user.tareas_habilitadas === true || user.tareas_habilitadas === 1);
  const kpisHabilitado = user && (user.dashboard_kpis_habilitado === true || user.dashboard_kpis_habilitado === 1);

  const revenueTrend = facturacionStats ? getTrend(facturacionStats.facturado_mes_actual, facturacionStats.facturado_mes_anterior) : null;
  const sessionsTrend = getTrend(stats.citasCompletadas, stats.sesionesUltimoMes);
  const clientRatio = clientesActivos + stats.clientesInactivos > 0
    ? Math.round((clientesActivos / (clientesActivos + stats.clientesInactivos)) * 100) : 100;
  const revenueChart = getRevenueChartData();

  // — Widget definitions —
  const ALL_WIDGETS = [
    { id: 'kpis', label: 'Métricas Principales', icon: '📊', size: 'full' },
    { id: 'quickStats', label: 'Estadísticas Rápidas', icon: '⚡', size: 'full' },
    { id: 'facturacion', label: 'Facturación', icon: '💰', size: 'full' },
    { id: 'oportunidades', label: 'Pipeline Oportunidades', icon: '🎯', size: 'full' },
    { id: 'citas', label: 'Citas Próximas', icon: '📅', size: 'full' },
    { id: 'comparativa', label: kpisHabilitado ? 'Comparativa Mensual' : 'Resumen Mensual', icon: '📈', size: 'half' },
    { id: 'tareas', label: 'Tareas Próximas', icon: '✅', size: 'half' },
    { id: 'cumpleanos', label: 'Cumpleaños', icon: '🎂', size: 'half' },
    { id: 'resumenGlobal', label: 'Resumen Global', icon: '📋', size: 'half' },
  ];

  const DEFAULT_ORDER = ALL_WIDGETS.map(w => w.id);
  const DEFAULT_HIDDEN = [];

  const storageKey = `dashboard-config-${user?.id || 'default'}`;

  const saveDashboardConfig = (cfg) => {
    setDashConfig(cfg);
    try { localStorage.setItem(storageKey, JSON.stringify(cfg)); } catch { /* ignore */ }
  };

  const toggleWidgetVisibility = (widgetId) => {
    const hidden = dashConfig.hidden.includes(widgetId)
      ? dashConfig.hidden.filter(id => id !== widgetId)
      : [...dashConfig.hidden, widgetId];
    saveDashboardConfig({ ...dashConfig, hidden });
  };

  const handleWidgetDragStart = (e, widgetId) => {
    setDragWidget(widgetId);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.4';
  };

  const handleWidgetDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragWidget(null);
    setDragOverWidget(null);
  };

  const handleWidgetDragOver = (e, widgetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (widgetId !== dragWidget) setDragOverWidget(widgetId);
  };

  const handleWidgetDrop = (e, targetId) => {
    e.preventDefault();
    setDragOverWidget(null);
    if (!dragWidget || dragWidget === targetId) return;
    const order = [...dashConfig.order];
    const fromIdx = order.indexOf(dragWidget);
    const toIdx = order.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    order.splice(fromIdx, 1);
    order.splice(toIdx, 0, dragWidget);
    saveDashboardConfig({ ...dashConfig, order });
    setDragWidget(null);
  };

  const resetDashboardConfig = () => {
    saveDashboardConfig({ order: DEFAULT_ORDER, hidden: DEFAULT_HIDDEN });
  };

  const isWidgetAvailable = (id) => {
    if (id === 'facturacion') return facHabilitada && facturacionStats;
    if (id === 'oportunidades') return oportHabilitadas && oportunidadesStats;
    if (id === 'tareas') return tareasHabilitadas;
    if (id === 'quickStats') return kpisHabilitado;
    return true;
  };

  // — Widget renderers —
  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'kpis':
        return kpisHabilitado ? (
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-icon kpi-icon-blue">📅</div>
              <div className="kpi-body">
                <div className="kpi-value">{stats.citasHoy}</div>
                <div className="kpi-label">Citas Hoy</div>
              </div>
              <Link to="/agenda" className="stat-link">Ver →</Link>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon kpi-icon-teal">📆</div>
              <div className="kpi-body">
                <div className="kpi-value">{stats.citasManana}</div>
                <div className="kpi-label">Citas Mañana</div>
              </div>
              <Link to="/agenda" className="stat-link">Ver →</Link>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon kpi-icon-green">💬</div>
              <div className="kpi-body">
                <div className="kpi-value">{stats.citasCompletadas}</div>
                <div className="kpi-label">{labels.sesiones} Este Mes</div>
                {sessionsTrend.dir !== 'neutral' && (
                  <span className={`kpi-trend kpi-trend-${sessionsTrend.dir}`}>
                    {sessionsTrend.dir === 'up' ? '▲' : '▼'} {sessionsTrend.pct}%
                  </span>
                )}
              </div>
              <Link to="/sesiones" className="stat-link">Ver →</Link>
            </div>
            <div className="kpi-card">
              <div className="kpi-icon kpi-icon-purple">👥</div>
              <div className="kpi-body">
                <div className="kpi-value">{clientesActivos}</div>
                <div className="kpi-label">Clientes Activos</div>
                <span className="kpi-sub">{clientRatio}% retención</span>
              </div>
              <Link to="/clientes" className="stat-link">Ver →</Link>
            </div>
            {facHabilitada && facturacionStats && (
              <div className="kpi-card kpi-card-wide">
                <div className="kpi-icon kpi-icon-emerald">💰</div>
                <div className="kpi-body">
                  <div className="kpi-value">{formatCurrency(facturacionStats.facturado_mes_actual)}</div>
                  <div className="kpi-label">Ingresos Este Mes</div>
                  {revenueTrend && revenueTrend.dir !== 'neutral' && (
                    <span className={`kpi-trend kpi-trend-${revenueTrend.dir}`}>
                      {revenueTrend.dir === 'up' ? '▲' : '▼'} {revenueTrend.pct}% vs mes anterior
                    </span>
                  )}
                </div>
                <Link to="/facturacion" className="stat-link">Ver →</Link>
              </div>
            )}
          </div>
        ) : (
          <div className="dashboard-stats-grid">
            <div className="dashboard-stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-value">{clientesActivos}</div>
                <div className="stat-label">Clientes Activos</div>
              </div>
              <Link to="/clientes" className="stat-link">Ver →</Link>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <div className="stat-value">{stats.citasHoy}</div>
                <div className="stat-label">Citas Hoy</div>
              </div>
              <Link to="/agenda" className="stat-link">Ver →</Link>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">📆</div>
              <div className="stat-content">
                <div className="stat-value">{stats.citasManana}</div>
                <div className="stat-label">Citas Mañana</div>
              </div>
              <Link to="/agenda" className="stat-link">Ver →</Link>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">💬</div>
              <div className="stat-content">
                <div className="stat-value">{stats.sesionesEsteMes}</div>
                <div className="stat-label">{labels.sesiones} Este Mes</div>
              </div>
              <Link to="/sesiones" className="stat-link">Ver →</Link>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <div className="stat-value">{stats.citasConfirmadas}</div>
                <div className="stat-label">Confirmadas</div>
              </div>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">❌</div>
              <div className="stat-content">
                <div className="stat-value">{stats.citasCanceladas}</div>
                <div className="stat-label">Canceladas</div>
              </div>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">🏠</div>
              <div className="stat-content">
                <div className="stat-value">{stats.modalidadPresencial}</div>
                <div className="stat-label">Clientes Presenciales</div>
              </div>
            </div>
            <div className="dashboard-stat-card">
              <div className="stat-icon">💻</div>
              <div className="stat-content">
                <div className="stat-value">{stats.modalidadOnline}</div>
                <div className="stat-label">Clientes Online</div>
              </div>
            </div>
            {stats.clientesInactivos > 0 && (
              <div className="dashboard-stat-card dashboard-stat-card-warning">
                <div className="stat-icon">⚠️</div>
                <div className="stat-content">
                  <div className="stat-value">{stats.clientesInactivos}</div>
                  <div className="stat-label">Clientes Inactivos</div>
                </div>
                <Link to="/clientes" className="stat-link">Ver →</Link>
              </div>
            )}
          </div>
        );

      case 'quickStats':
        return (
          <div className="quick-stats-row">
            <div className="quick-stat"><span className="quick-stat-val">{stats.citasConfirmadas}</span><span className="quick-stat-lbl">Citas Confirmadas</span></div>
            <div className="quick-stat"><span className="quick-stat-val">{stats.citasCanceladas}</span><span className="quick-stat-lbl">Citas Canceladas</span></div>
            <div className="quick-stat"><span className="quick-stat-val">{stats.modalidadPresencial}</span><span className="quick-stat-lbl">Clientes Presenciales</span></div>
            <div className="quick-stat"><span className="quick-stat-val">{stats.modalidadOnline}</span><span className="quick-stat-lbl">Clientes Online</span></div>
            <div className="quick-stat"><span className="quick-stat-val">{stats.sesionesPresencial}</span><span className="quick-stat-lbl">{labels.sesiones} Presenciales</span></div>
            <div className="quick-stat"><span className="quick-stat-val">{stats.sesionesOnline}</span><span className="quick-stat-lbl">{labels.sesiones} Online</span></div>
            {stats.clientesInactivos > 0 && (
              <div className="quick-stat quick-stat-warn"><span className="quick-stat-val">{stats.clientesInactivos}</span><span className="quick-stat-lbl">Clientes Inactivos</span></div>
            )}
          </div>
        );

      case 'facturacion':
        return (
          <div className={kpisHabilitado ? "dashboard-row" : "facturacion-widget-dashboard"}>
            <div className="dashboard-card" style={kpisHabilitado ? { flex: 1 } : {}}>
              <div className="dashboard-card-header">
                <h3>Facturación</h3>
                <Link to="/facturacion" className="dashboard-link-small">Ver todo →</Link>
              </div>
              <div className="dashboard-card-content">
                <div className="facturacion-widget-grid">
                  <div className="facturacion-stat-card" style={{ borderLeftColor: '#007bff' }}>
                    <div className="facturacion-stat-title">TOTAL FACTURADO</div>
                    <div className="facturacion-stat-value">{formatCurrency(facturacionStats.total_facturado)}</div>
                    <div className="facturacion-stat-desc">Todas las facturas pagadas</div>
                  </div>
                  {!kpisHabilitado && (
                    <div className="facturacion-stat-card" style={{ borderLeftColor: '#28a745' }}>
                      <div className="facturacion-stat-title">ESTE MES</div>
                      <div className="facturacion-stat-value">{formatCurrency(facturacionStats.facturado_mes_actual)}</div>
                      <div className="facturacion-stat-desc">Facturado en {new Date().toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}</div>
                    </div>
                  )}
                  <div className="facturacion-stat-card" style={{ borderLeftColor: '#17a2b8' }}>
                    <div className="facturacion-stat-title">ESTE AÑO</div>
                    <div className="facturacion-stat-value">{formatCurrency(facturacionStats.facturado_ano_actual)}</div>
                    <div className="facturacion-stat-desc">Facturado en {new Date().getFullYear()}</div>
                  </div>
                  <div className="facturacion-stat-card" style={{ borderLeftColor: '#ffc107' }}>
                    <div className="facturacion-stat-title">PENDIENTE</div>
                    <div className="facturacion-stat-value">{formatCurrency(facturacionStats.total_pendiente)}</div>
                    <div className="facturacion-stat-desc">{facturacionStats.facturas_pendientes || 0} {facturacionStats.facturas_pendientes === 1 ? 'factura' : 'facturas'}</div>
                  </div>
                  {facturacionStats.facturas_vencidas > 0 && (
                    <div className="facturacion-stat-card" style={{ borderLeftColor: '#dc3545' }}>
                      <div className="facturacion-stat-title">VENCIDO</div>
                      <div className="facturacion-stat-value" style={{ color: '#dc3545' }}>{formatCurrency(facturacionStats.total_vencido)}</div>
                      <div className="facturacion-stat-desc">{facturacionStats.facturas_vencidas} vencida(s)</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {kpisHabilitado && revenueChart.length > 0 && (
              <div className="dashboard-card" style={{ minWidth: 280, maxWidth: 400 }}>
                <div className="dashboard-card-header"><h3>Ingresos Mensuales</h3></div>
                <div className="dashboard-card-content">
                  <div className="revenue-chart">
                    {revenueChart.map((m, i) => (
                      <div key={i} className="revenue-bar-wrapper">
                        <div className="revenue-bar-label">{formatCurrency(m.value)}</div>
                        <div className="revenue-bar-track"><div className="revenue-bar-fill" style={{ height: `${m.pct}%` }} /></div>
                        <div className="revenue-bar-month">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'oportunidades':
        return (
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>Pipeline Oportunidades</h3>
              <Link to="/oportunidades" className="dashboard-link-small">Ver todo →</Link>
            </div>
            <div className="dashboard-card-content">
              <div className="oport-pipeline">
                {(oportunidadesStats.por_etapa || []).map((e, i) => (
                  <div key={i} className="oport-stage">
                    <div className="oport-stage-count">{e.count ?? e.total ?? 0}</div>
                    <div className="oport-stage-label">{e.etapa}</div>
                    {e.valor_total > 0 && <div className="oport-stage-val">{formatCurrency(e.valor_total)}</div>}
                  </div>
                ))}
              </div>
              {oportunidadesStats.tasa_conversion != null && (
                <div className="oport-conversion">Tasa de conversión: <strong>{oportunidadesStats.tasa_conversion}%</strong></div>
              )}
            </div>
          </div>
        );

      case 'citas':
        return (
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>Citas Próximas 2 Semanas</h3>
              <Link to="/agenda" className="dashboard-link-small">Ver todas →</Link>
            </div>
            <div className="dashboard-card-content">
              {citas.length === 0 ? (
                <div className="empty-state"><p>No hay citas programadas en las próximas 2 semanas</p></div>
              ) : (
                <div className="calendario-mini">
                  {diasSemanas.map((dia, index) => {
                    const citasDia = getCitasPorDia(dia);
                    const esHoy = dia.toDateString() === new Date().toDateString();
                    return (
                      <div key={index} className={`calendario-mini-dia ${esHoy ? 'hoy' : ''}`}>
                        <div className="calendario-mini-fecha">{formatearFecha(dia)}</div>
                        <div className="calendario-mini-citas">
                          {citasDia.map(cita => {
                            const fechaHora = new Date(cita.fecha_hora);
                            return (
                              <div key={cita.id} className="cita-mini-dashboard"
                                style={{ backgroundColor: getClienteColor(cita.cliente_id) }}
                                title={`${cita.titulo} - ${getClienteNombre(cita.cliente_id)} - ${fechaHora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}>
                                <span className="cita-mini-hora-dashboard">{fechaHora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                                <span className="cita-mini-titulo-dashboard">{cita.titulo}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );

      case 'comparativa':
        return kpisHabilitado ? (
          <div className="dashboard-card">
            <div className="dashboard-card-header"><h3>Comparativa Mensual</h3></div>
            <div className="dashboard-card-content">
              <div className="summary-stats">
                <div className="summary-item"><span className="summary-label">{labels.sesiones} Mes Actual:</span><span className="summary-value">{stats.citasCompletadas}</span></div>
                <div className="summary-item"><span className="summary-label">{labels.sesiones} Mes Anterior:</span><span className="summary-value">{stats.sesionesUltimoMes ?? 0}</span></div>
                <div className="summary-item"><span className="summary-label">Horas Mes Actual:</span><span className="summary-value">{formatTiempo(stats.tiempoMesActual ?? 0)}</span></div>
                <div className="summary-item"><span className="summary-label">Horas Mes Anterior:</span><span className="summary-value">{formatTiempo(stats.tiempoUltimoMes ?? 0)}</span></div>
                {facHabilitada && facturacionStats && (
                  <>
                    <div className="summary-item"><span className="summary-label">Nuevos Clientes Mes:</span><span className="summary-value">{facturacionStats.nuevos_clientes_mes ?? 0}</span></div>
                    <div className="summary-item"><span className="summary-label">Nuevos Mes Anterior:</span><span className="summary-value">{facturacionStats.nuevos_clientes_mes_anterior ?? 0}</span></div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="dashboard-card">
              <div className="dashboard-card-header"><h3>Resumen Mes Actual</h3></div>
              <div className="dashboard-card-content">
                <div className="summary-stats">
                  <div className="summary-item"><span className="summary-label">{labels.sesiones} Completadas:</span><span className="summary-value">{stats.citasCompletadas}</span></div>
                  <div className="summary-item"><span className="summary-label">{labels.tipo_label === 'Comercial' ? 'Total Horas:' : 'Total Horas Practicas:'}</span><span className="summary-value">{formatTiempo(stats.tiempoMesActual ?? 0)}</span></div>
                </div>
              </div>
            </div>
            <div className="dashboard-card">
              <div className="dashboard-card-header"><h3>Resumen Último Mes</h3></div>
              <div className="dashboard-card-content">
                <div className="summary-stats">
                  <div className="summary-item"><span className="summary-label">{labels.sesiones} Completadas:</span><span className="summary-value">{stats.sesionesUltimoMes ?? 0}</span></div>
                  <div className="summary-item"><span className="summary-label">{labels.tipo_label === 'Comercial' ? 'Total Horas:' : 'Total Horas Practicas:'}</span><span className="summary-value">{formatTiempo(stats.tiempoUltimoMes ?? 0)}</span></div>
                </div>
              </div>
            </div>
          </>
        );

      case 'tareas':
        return (
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h3>Tareas Próximas</h3>
              <Link to="/tareas" className="dashboard-link-small">Ver todas →</Link>
            </div>
            <div className="dashboard-card-content">
              {tareasProximas.length === 0 ? (
                <div className="empty-state"><p>No hay tareas pendientes en las próximas 2 semanas</p></div>
              ) : (
                <div className="tareas-list-dashboard">
                  {tareasProximas.slice(0, 8).map((tarea) => {
                    const fechaLimite = tarea.fecha_limite ? new Date(tarea.fecha_limite) : null;
                    const fechaRecordatorio = tarea.fecha_recordatorio ? new Date(tarea.fecha_recordatorio) : null;
                    const hoy = new Date(); hoy.setHours(0,0,0,0);
                    const esVencida = fechaLimite && fechaLimite < hoy;
                    return (
                      <div key={tarea.id} className={`tarea-item-dashboard ${esVencida ? 'vencida' : ''} prioridad-${tarea.prioridad}`}>
                        <div className="tarea-header-dashboard">
                          <span className={`tipo-badge tipo-${tarea.tipo}`}>{tarea.tipo}</span>
                          <span className={`prioridad-badge prioridad-${tarea.prioridad}`}>{tarea.prioridad}</span>
                          <strong className="tarea-titulo-dashboard">{tarea.titulo}</strong>
                        </div>
                        {tarea.cliente_nombre && <div className="tarea-cliente-dashboard">👤 {tarea.cliente_nombre} {tarea.cliente_apellidos}</div>}
                        <div className="tarea-fecha-dashboard">
                          {fechaLimite && <span>📅 {fechaLimite.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>}
                          {fechaRecordatorio && <span style={{ marginLeft: '12px' }}>🔔 {fechaRecordatorio.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {tareasProximas.length > 8 && (
                    <div style={{ textAlign: 'center', marginTop: '12px' }}>
                      <Link to="/tareas" className="btn btn-secondary btn-sm">Ver {tareasProximas.length - 8} más →</Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'cumpleanos':
        return (
          <div className="dashboard-card">
            <div className="dashboard-card-header"><h3>🎂 Cumpleaños</h3></div>
            <div className="dashboard-card-content">
              {cumpleanos.length === 0 ? (
                <div className="empty-state"><p>No hay cumpleaños en las próximas 2 semanas</p></div>
              ) : (
                <div className="cumpleanos-list">
                  {cumpleanos.map((cliente, index) => (
                    <div key={cliente.id || index} className="cumpleanos-item">
                      <div className="cumpleanos-fecha">{cliente.fechaCumpleanos.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</div>
                      <div className="cumpleanos-nombre">{cliente.nombre} {cliente.apellidos}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'resumenGlobal':
        return (
          <div className="dashboard-card">
            <div className="dashboard-card-header"><h3>Resumen Global</h3></div>
            <div className="dashboard-card-content">
              <div className="summary-stats">
                <div className="summary-item"><span className="summary-label">Total {labels.sesiones}:</span><span className="summary-value">{stats.totalCitasCompletadas ?? 0}</span></div>
                <div className="summary-item"><span className="summary-label">Tiempo Total:</span><span className="summary-value">{formatTiempo(stats.tiempoTotalInvertido ?? 0)}</span></div>
                <div className="summary-item"><span className="summary-label">{labels.sesiones} Online:</span><span className="summary-value">{stats.sesionesOnline}</span></div>
                <div className="summary-item"><span className="summary-label">{labels.sesiones} Presenciales:</span><span className="summary-value">{stats.sesionesPresencial}</span></div>
              </div>
              <Link to="/sesiones" className="dashboard-link-small" style={{ marginTop: '15px', display: 'block' }}>Ver todas →</Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const visibleWidgets = dashConfig.order.filter(id => !dashConfig.hidden.includes(id) && isWidgetAvailable(id));

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        <button
          type="button"
          className={`btn ${customizeMode ? 'btn-primary' : 'btn-secondary'} btn-sm`}
          onClick={() => setCustomizeMode(!customizeMode)}
        >
          {customizeMode ? 'Listo' : 'Personalizar'}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {customizeMode && (
        <div className="widget-config-panel">
          <div className="widget-config-header">
            <span>Arrastra para reordenar. Marca o desmarca para mostrar u ocultar.</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={resetDashboardConfig}>Restaurar orden</button>
          </div>
          <div className="widget-config-list">
            {dashConfig.order.filter(id => isWidgetAvailable(id)).map((widgetId) => {
              const def = ALL_WIDGETS.find(w => w.id === widgetId);
              if (!def) return null;
              const hidden = dashConfig.hidden.includes(widgetId);
              return (
                <div
                  key={widgetId}
                  className={`widget-config-item ${dragOverWidget === widgetId ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={(e) => handleWidgetDragStart(e, widgetId)}
                  onDragEnd={handleWidgetDragEnd}
                  onDragOver={(e) => handleWidgetDragOver(e, widgetId)}
                  onDrop={(e) => handleWidgetDrop(e, widgetId)}
                >
                  <span className="widget-drag-handle">⠿</span>
                  <label className="widget-config-label">
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={() => toggleWidgetVisibility(widgetId)}
                    />
                    <span className="widget-config-icon">{def.icon}</span>
                    {def.label}
                  </label>
                  <span className="widget-config-size">{def.size === 'full' ? 'Ancho completo' : 'Media'}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="widget-grid">
        {visibleWidgets.map((widgetId) => {
          const def = ALL_WIDGETS.find(w => w.id === widgetId);
          if (!def) return null;
          return (
            <div
              key={widgetId}
              className={`widget-slot widget-${def.size} ${customizeMode ? 'widget-editable' : ''} ${dragOverWidget === widgetId && !customizeMode ? '' : ''}`}
              draggable={customizeMode}
              onDragStart={customizeMode ? (e) => handleWidgetDragStart(e, widgetId) : undefined}
              onDragEnd={customizeMode ? handleWidgetDragEnd : undefined}
              onDragOver={customizeMode ? (e) => handleWidgetDragOver(e, widgetId) : undefined}
              onDrop={customizeMode ? (e) => handleWidgetDrop(e, widgetId) : undefined}
            >
              {customizeMode && <div className="widget-drag-bar">⠿ {def.icon} {def.label}</div>}
              {renderWidget(widgetId)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Usar React.memo para evitar re-renderizados innecesarios
export default React.memo(Dashboard);

